import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Switch,
  FormControlLabel,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ImageIcon from '@mui/icons-material/Image';
import ServiceCategoryService, { ServiceCategory } from '../services/api/ServiceCategoryService';
import ServiceService, { Service } from '../services/api/ServiceService';
import apiClient from '../services/api/apiClient';

// ── Duration presets ────────────────────────────────────────────────────────
const DURATION_PRESETS = [
  { label: '30 minutes', value: 30 },
  { label: '1 hour', value: 60 },
  { label: '1.5 hours', value: 90 },
  { label: '2 hours', value: 120 },
  { label: '3 hours', value: 180 },
  { label: '4 hours', value: 240 },
  { label: 'Half day (6 h)', value: 360 },
  { label: 'Full day (8 h)', value: 480 },
  { label: 'Custom', value: 'custom' },
];

// ── Icon upload field ────────────────────────────────────────────────────────
interface IconUploadFieldProps {
  currentUrl: string;
  onChange: (url: string) => void;
}

function IconUploadField({ currentUrl, onChange }: IconUploadFieldProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string>(currentUrl || '');

  // Sync when editing an existing record
  useEffect(() => { setPreview(currentUrl || ''); }, [currentUrl]);

  const handleFile = async (file: File) => {
    if (!['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'].includes(file.type)) {
      setError('Use PNG, JPG, WEBP, or SVG');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError('Max file size is 2 MB');
      return;
    }

    setError(null);
    setUploading(true);

    // Show local preview immediately
    const localUrl = URL.createObjectURL(file);
    setPreview(localUrl);

    try {
      const form = new FormData();
      form.append('icon', file);
      const res = await apiClient.post('/services/admin/upload-icon/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const uploadedUrl: string = res.data?.data?.icon_url ?? res.data?.icon_url ?? '';
      setPreview(uploadedUrl);
      onChange(uploadedUrl);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Upload failed — check your storage config');
      setPreview(currentUrl || '');
      onChange(currentUrl || '');
    } finally {
      URL.revokeObjectURL(localUrl);
      setUploading(false);
    }
  };

  return (
    <Box>
      <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>
        Icon image
      </Typography>

      {/* Drop zone */}
      <Box
        onClick={() => !uploading && fileRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const file = e.dataTransfer.files?.[0];
          if (file) handleFile(file);
        }}
        sx={{
          border: '2px dashed',
          borderColor: error ? 'error.main' : 'grey.300',
          borderRadius: 2,
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: uploading ? 'default' : 'pointer',
          minHeight: 110,
          bgcolor: 'grey.50',
          transition: 'border-color .2s',
          '&:hover': { borderColor: uploading ? 'grey.300' : 'primary.main' },
        }}
      >
        {uploading ? (
          <CircularProgress size={32} />
        ) : preview ? (
          <Tooltip title="Click or drag to replace">
            <Box
              component="img"
              src={preview}
              alt="Icon preview"
              sx={{ maxHeight: 80, maxWidth: '100%', objectFit: 'contain', borderRadius: 1 }}
            />
          </Tooltip>
        ) : (
          <>
            <CloudUploadIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 0.5 }} />
            <Typography variant="caption" color="textSecondary" align="center">
              Drag & drop or click to upload<br />
              PNG · JPG · WEBP · SVG · max 2 MB
            </Typography>
          </>
        )}
      </Box>

      {preview && !uploading && (
        <Button
          size="small"
          sx={{ mt: 0.5 }}
          onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}
          startIcon={<ImageIcon />}
        >
          Replace image
        </Button>
      )}

      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
          {error}
        </Typography>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/svg+xml"
        hidden
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
      />
    </Box>
  );
}

// ── Duration picker ──────────────────────────────────────────────────────────
interface DurationPickerProps {
  value: string;
  onChange: (val: string) => void;
}

function DurationPicker({ value, onChange }: DurationPickerProps) {
  const isPreset = DURATION_PRESETS.some((p) => p.value !== 'custom' && String(p.value) === value);
  const [mode, setMode] = useState<'preset' | 'custom'>(
    value && !isPreset ? 'custom' : 'preset'
  );
  const selectVal = mode === 'custom' ? 'custom' : (value || '');

  const handleSelect = (selected: string | number) => {
    if (selected === 'custom') {
      setMode('custom');
      onChange('');
    } else {
      setMode('preset');
      onChange(String(selected));
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <TextField
        select
        label="Duration"
        value={selectVal}
        onChange={(e) => handleSelect(e.target.value)}
        sx={{ flex: 1 }}
      >
        <MenuItem value=""><em>Not specified</em></MenuItem>
        {DURATION_PRESETS.map((p) => (
          <MenuItem key={p.value} value={String(p.value)}>{p.label}</MenuItem>
        ))}
      </TextField>

      {mode === 'custom' && (
        <TextField
          label="Minutes"
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          sx={{ width: 110 }}
          inputProps={{ min: 1 }}
          placeholder="e.g. 150"
        />
      )}
    </Box>
  );
}

// ── Tab panel ────────────────────────────────────────────────────────────────
interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }
function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function ServiceManagementPage() {
  const [tabValue, setTabValue] = useState(0);

  // Categories
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const [categorySearch, setCategorySearch] = useState('');
  const [categoryTotal, setCategoryTotal] = useState(0);
  const [categoryPagination, setCategoryPagination] = useState({ pageSize: 10, page: 0 });

  // Services
  const [services, setServices] = useState<Service[]>([]);
  const [serviceLoading, setServiceLoading] = useState(false);
  const [serviceError, setServiceError] = useState<string | null>(null);
  const [serviceSearch, setServiceSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [serviceTotal, setServiceTotal] = useState(0);
  const [servicePagination, setServicePagination] = useState({ pageSize: 10, page: 0 });

  // Dialogs
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [openServiceDialog, setOpenServiceDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'category' | 'service'; id: string; name: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Forms
  const [editingCategory, setEditingCategory] = useState<ServiceCategory | null>(null);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [categoryFormData, setCategoryFormData] = useState({
    name: '', slug: '', description: '', icon_url: '', is_active: true,
  });
  const [serviceFormData, setServiceFormData] = useState({
    category: '', name: '', slug: '', description: '', icon_url: '',
    base_price: '', duration_minutes: '', is_active: true,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadCategories(); }, [categorySearch]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadServices(); }, [serviceSearch, selectedCategory]);

  const loadCategories = async (page?: number, limit = 10) => {
    try {
      setCategoryLoading(true);
      setCategoryError(null);
      const data = await ServiceCategoryService.listCategories(page !== undefined ? page + 1 : 1, limit, categorySearch);
      setCategories(data.items);
      setCategoryTotal(data.total_count);
    } catch (err: any) {
      setCategoryError(err?.message || 'Failed to load categories');
    } finally {
      setCategoryLoading(false);
    }
  };

  const loadServices = async (page?: number, limit = 10) => {
    try {
      setServiceLoading(true);
      setServiceError(null);
      const data = await ServiceService.listServices(page !== undefined ? page + 1 : 1, limit, serviceSearch, selectedCategory);
      setServices(data.items);
      setServiceTotal(data.total_count);
    } catch (err: any) {
      setServiceError(err?.message || 'Failed to load services');
    } finally {
      setServiceLoading(false);
    }
  };

  const generateSlug = (name: string) =>
    name.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^\w-]/g, '');

  // ── Category handlers ────────────────────────────────────────────────────
  const handleAddCategory = () => {
    setEditingCategory(null);
    setCategoryFormData({ name: '', slug: '', description: '', icon_url: '', is_active: true });
    setOpenCategoryDialog(true);
  };

  const handleEditCategory = (cat: ServiceCategory) => {
    setEditingCategory(cat);
    setCategoryFormData({
      name: cat.name, slug: cat.slug, description: cat.description || '',
      icon_url: cat.icon_url || '', is_active: cat.is_active,
    });
    setOpenCategoryDialog(true);
  };

  const handleSaveCategory = async () => {
    if (!categoryFormData.name.trim()) { setCategoryError('Category name is required'); return; }
    const slug = categoryFormData.slug.trim() || generateSlug(categoryFormData.name);
    if (!slug) { setCategoryError('Could not generate slug from name'); return; }
    try {
      setSubmitting(true);
      setCategoryError(null);
      const payload = { ...categoryFormData, slug };
      if (editingCategory) {
        await ServiceCategoryService.updateCategory(editingCategory.id, payload);
      } else {
        await ServiceCategoryService.createCategory(payload);
      }
      setOpenCategoryDialog(false);
      setCategories([]);
      await new Promise(r => setTimeout(r, 300));
      await loadCategories();
    } catch (err: any) {
      setCategoryError(err?.message || 'Failed to save category');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Service handlers ─────────────────────────────────────────────────────
  const handleAddService = () => {
    setEditingService(null);
    setServiceFormData({ category: selectedCategory, name: '', slug: '', description: '', icon_url: '', base_price: '', duration_minutes: '', is_active: true });
    setOpenServiceDialog(true);
  };

  const handleEditService = (svc: Service) => {
    setEditingService(svc);
    setServiceFormData({
      category: svc.category, name: svc.name, slug: svc.slug,
      description: svc.description || '', icon_url: svc.icon_url || '',
      base_price: svc.base_price?.toString() || '',
      duration_minutes: svc.duration_minutes?.toString() || '',
      is_active: svc.is_active,
    });
    setOpenServiceDialog(true);
  };

  const handleSaveService = async () => {
    if (!serviceFormData.name.trim()) { setServiceError('Service name is required'); return; }
    if (!serviceFormData.category) { setServiceError('Category is required'); return; }
    const slug = serviceFormData.slug.trim() || generateSlug(serviceFormData.name);
    if (!slug) { setServiceError('Could not generate slug from name'); return; }
    try {
      setSubmitting(true);
      setServiceError(null);
      const data = {
        ...serviceFormData, slug,
        base_price: serviceFormData.base_price ? parseFloat(serviceFormData.base_price) : null,
        duration_minutes: serviceFormData.duration_minutes ? parseInt(serviceFormData.duration_minutes) : null,
      };
      if (editingService) {
        await ServiceService.updateService(editingService.id, data);
      } else {
        await ServiceService.createService(data);
      }
      setOpenServiceDialog(false);
      setServices([]);
      await new Promise(r => setTimeout(r, 300));
      await loadServices();
    } catch (err: any) {
      setServiceError(err?.message || 'Failed to save service');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────────
  const handleDeleteClick = (type: 'category' | 'service', id: string, name: string) => {
    setDeleteTarget({ type, id, name });
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setSubmitting(true);
      if (deleteTarget.type === 'category') {
        await ServiceCategoryService.deleteCategory(deleteTarget.id);
        setCategoryError(null);
        await loadCategories();
      } else {
        await ServiceService.deleteService(deleteTarget.id);
        setServiceError(null);
        await loadServices();
      }
      setOpenDeleteDialog(false);
    } catch (err: any) {
      if (deleteTarget.type === 'category') setCategoryError(err?.message || 'Failed to delete');
      else setServiceError(err?.message || 'Failed to delete');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Columns ───────────────────────────────────────────────────────────────
  const categoryColumns: GridColDef[] = [
    {
      field: 'icon_url', headerName: 'Icon', width: 60, sortable: false,
      renderCell: (p) => p.value
        ? <Box component="img" src={p.value} alt="" sx={{ height: 32, width: 32, objectFit: 'contain' }} />
        : <ImageIcon sx={{ color: 'text.disabled' }} />,
    },
    { field: 'name', headerName: 'Name', flex: 1 },
    { field: 'slug', headerName: 'Slug', flex: 1 },
    { field: 'service_count', headerName: 'Services', width: 90, align: 'center', headerAlign: 'center' },
    {
      field: 'is_active', headerName: 'Active', width: 90, align: 'center', headerAlign: 'center',
      renderCell: (p) => (
        <Box sx={{ color: p.value ? 'success.main' : 'text.disabled', fontWeight: 600 }}>
          {p.value ? 'Yes' : 'No'}
        </Box>
      ),
    },
    {
      field: 'actions', headerName: 'Actions', width: 160, sortable: false,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => handleEditCategory(p.row)}>Edit</Button>
          <Button size="small" color="error" variant="outlined" startIcon={<DeleteIcon />} onClick={() => handleDeleteClick('category', p.row.id, p.row.name)}>Delete</Button>
        </Box>
      ),
    },
  ];

  const serviceColumns: GridColDef[] = [
    {
      field: 'icon_url', headerName: 'Icon', width: 60, sortable: false,
      renderCell: (p) => p.value
        ? <Box component="img" src={p.value} alt="" sx={{ height: 32, width: 32, objectFit: 'contain' }} />
        : <ImageIcon sx={{ color: 'text.disabled' }} />,
    },
    { field: 'name', headerName: 'Name', flex: 1 },
    { field: 'slug', headerName: 'Slug', flex: 1 },
    {
      field: 'base_price', headerName: 'Price', width: 110, align: 'center', headerAlign: 'center',
      renderCell: (p) => p.value ? `₦${Number(p.value).toLocaleString()}` : '—',
    },
    {
      field: 'duration_minutes', headerName: 'Duration', width: 110, align: 'center', headerAlign: 'center',
      renderCell: (p) => {
        if (!p.value) return '—';
        const mins = Number(p.value);
        return mins >= 60 ? `${(mins / 60).toFixed(mins % 60 === 0 ? 0 : 1)} h` : `${mins} min`;
      },
    },
    {
      field: 'is_active', headerName: 'Active', width: 90, align: 'center', headerAlign: 'center',
      renderCell: (p) => (
        <Box sx={{ color: p.value ? 'success.main' : 'text.disabled', fontWeight: 600 }}>
          {p.value ? 'Yes' : 'No'}
        </Box>
      ),
    },
    {
      field: 'actions', headerName: 'Actions', width: 160, sortable: false,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => handleEditService(p.row)}>Edit</Button>
          <Button size="small" color="error" variant="outlined" startIcon={<DeleteIcon />} onClick={() => handleDeleteClick('service', p.row.id, p.row.name)}>Delete</Button>
        </Box>
      ),
    },
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Card sx={{ mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', position: 'relative', overflow: 'hidden' }}>
        <CardContent>
          <Typography variant="h4" fontWeight={700} gutterBottom>Service Management</Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Manage service categories and individual services for your marketplace
          </Typography>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab label="Categories" />
          <Tab label="Services" />
        </Tabs>
      </Box>

      {/* ── Categories tab ── */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
          <TextField placeholder="Search categories…" value={categorySearch} onChange={(e) => setCategorySearch(e.target.value)} size="small" sx={{ flexGrow: 1 }} />
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddCategory}>Add Category</Button>
        </Box>

        {categoryError && <Alert severity="error" sx={{ mb: 2 }}>{categoryError}</Alert>}

        {categoryLoading
          ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          : (
            <Box sx={{ height: 600 }}>
              <DataGrid rows={categories} columns={categoryColumns} pageSizeOptions={[5, 10, 25]}
                paginationModel={categoryPagination} rowCount={categoryTotal} paginationMode="server"
                onPaginationModelChange={(m) => { setCategoryPagination(m); loadCategories(m.page, m.pageSize); }}
                sx={{ width: '100%', height: '100%' }} />
            </Box>
          )}
      </TabPanel>

      {/* ── Services tab ── */}
      <TabPanel value={tabValue} index={1}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={4}>
                <TextField select label="Select Category" value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} fullWidth size="small">
                  <MenuItem value="">All Categories</MenuItem>
                  {categories.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField placeholder="Search services…" value={serviceSearch} onChange={(e) => setServiceSearch(e.target.value)} size="small" fullWidth />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddService} fullWidth>
                  Add Service
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {serviceError && <Alert severity="error" sx={{ mb: 2 }}>{serviceError}</Alert>}

        {serviceLoading
          ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          : (
            <Box sx={{ height: 600 }}>
              <DataGrid rows={services} columns={serviceColumns} pageSizeOptions={[5, 10, 25]}
                paginationModel={servicePagination} rowCount={serviceTotal} paginationMode="server"
                onPaginationModelChange={(m) => { setServicePagination(m); loadServices(m.page, m.pageSize); }}
                sx={{ width: '100%', height: '100%' }} />
            </Box>
          )}
      </TabPanel>

      {/* ── Category dialog ── */}
      <Dialog open={openCategoryDialog} onClose={() => !submitting && setOpenCategoryDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingCategory ? 'Edit Category' : 'Add Category'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

            <TextField
              label="Category Name *"
              value={categoryFormData.name}
              onChange={(e) => {
                const name = e.target.value;
                const slug = !categoryFormData.slug || categoryFormData.slug === generateSlug(categoryFormData.name)
                  ? generateSlug(name) : categoryFormData.slug;
                setCategoryFormData((p) => ({ ...p, name, slug }));
              }}
              fullWidth
              placeholder="e.g. Plumbing"
            />

            <TextField
              label="Slug (auto-generated)"
              value={categoryFormData.slug}
              onChange={(e) => setCategoryFormData((p) => ({ ...p, slug: e.target.value }))}
              fullWidth
              helperText="Auto-generated from name, but can be customised"
            />

            <TextField
              label="Description"
              value={categoryFormData.description}
              onChange={(e) => setCategoryFormData((p) => ({ ...p, description: e.target.value }))}
              fullWidth multiline rows={3}
            />

            {/* Icon upload */}
            <IconUploadField
              currentUrl={categoryFormData.icon_url}
              onChange={(url) => setCategoryFormData((p) => ({ ...p, icon_url: url }))}
            />

            {/* Active toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1, py: 0.5, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {categoryFormData.is_active ? 'Active (visible to users)' : 'Draft (hidden from users)'}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Toggle to publish or hide this category
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={categoryFormData.is_active}
                    onChange={(e) => setCategoryFormData((p) => ({ ...p, is_active: e.target.checked }))}
                    color="success"
                  />
                }
                label=""
              />
            </Box>

          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCategoryDialog(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={handleSaveCategory} variant="contained" disabled={submitting}>
            {submitting ? <CircularProgress size={22} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Service dialog ── */}
      <Dialog open={openServiceDialog} onClose={() => !submitting && setOpenServiceDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingService ? 'Edit Service' : 'Add Service'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

            {/* Category selector — always visible so user can verify/change */}
            <TextField
              select
              label="Category *"
              value={serviceFormData.category}
              onChange={(e) => setServiceFormData((p) => ({ ...p, category: e.target.value }))}
              fullWidth
            >
              {categories.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </TextField>

            <TextField
              label="Service Name *"
              value={serviceFormData.name}
              onChange={(e) => {
                const name = e.target.value;
                const slug = !serviceFormData.slug || serviceFormData.slug === generateSlug(serviceFormData.name)
                  ? generateSlug(name) : serviceFormData.slug;
                setServiceFormData((p) => ({ ...p, name, slug }));
              }}
              fullWidth
              placeholder="e.g. Fix Leaky Tap"
            />

            <TextField
              label="Slug (auto-generated)"
              value={serviceFormData.slug}
              onChange={(e) => setServiceFormData((p) => ({ ...p, slug: e.target.value }))}
              fullWidth
              helperText="Auto-generated from name, but can be customised"
            />

            <TextField
              label="Description"
              value={serviceFormData.description}
              onChange={(e) => setServiceFormData((p) => ({ ...p, description: e.target.value }))}
              fullWidth multiline rows={3}
            />

            {/* Icon upload */}
            <IconUploadField
              currentUrl={serviceFormData.icon_url}
              onChange={(url) => setServiceFormData((p) => ({ ...p, icon_url: url }))}
            />

            {/* Base price with ₦ prefix */}
            <TextField
              label="Base Price"
              type="number"
              value={serviceFormData.base_price}
              onChange={(e) => setServiceFormData((p) => ({ ...p, base_price: e.target.value }))}
              fullWidth
              inputProps={{ step: '0.01', min: 0 }}
              InputProps={{
                startAdornment: <InputAdornment position="start">₦</InputAdornment>,
              }}
              helperText="Leave blank if price varies per job"
            />

            {/* Duration picker */}
            <DurationPicker
              value={serviceFormData.duration_minutes}
              onChange={(val) => setServiceFormData((p) => ({ ...p, duration_minutes: val }))}
            />

            {/* Active toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1, py: 0.5, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {serviceFormData.is_active ? 'Active (visible to users)' : 'Draft (hidden from users)'}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Toggle to publish or hide this service
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={serviceFormData.is_active}
                    onChange={(e) => setServiceFormData((p) => ({ ...p, is_active: e.target.checked }))}
                    color="success"
                  />
                }
                label=""
              />
            </Box>

          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenServiceDialog(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={handleSaveService} variant="contained" disabled={submitting}>
            {submitting ? <CircularProgress size={22} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete dialog ── */}
      <Dialog open={openDeleteDialog} onClose={() => !submitting && setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteTarget?.name}</strong>?
            {deleteTarget?.type === 'category' && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                This will also remove all services under this category.
              </Typography>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={submitting}>
            {submitting ? <CircularProgress size={22} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
